/**
 * Team Management API Client
 * Multi-User RBAC System - Phase 3 + Branch Assignments
 */

import axiosInstance from './axios-instance';
import type {
  TeamMembersResponse,
  InviteTeamMemberRequest,
  InviteResponse,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  UpdateMemberRoleRequest,
  UpdateRoleResponse,
  RemoveMemberResponse,
  CancelInvitationResponse,
  AssignBranchesRequest,
  AssignBranchesResponse,
  RemoveBranchResponse,
  MemberBranchesResponse,
  BranchMembersResponse,
  UserType,
} from '@/src/types/team';

// Re-export types for backward compatibility
export type {
  TeamMembersResponse,
  InviteTeamMemberRequest,
  InviteResponse,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  UpdateMemberRoleRequest,
  UpdateRoleResponse,
  RemoveMemberResponse,
  CancelInvitationResponse,
  AssignBranchesRequest,
  AssignBranchesResponse,
  RemoveBranchResponse,
  MemberBranchesResponse,
  BranchMembersResponse,
  UserType,
};

// Legacy type exports for backward compatibility
export interface TeamMember {
  id: string;
  userType: 'owner' | 'admin' | 'staff';
  roleCode: 'owner' | 'admin' | 'staff';
  email: string;
  fullName: string;
  phone: string | null;
  invitationAcceptedAt: string;
  lastLoginAt: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  roleCode: 'customer_admin' | 'customer_manager' | 'customer_user';
  expiresAt: string;
  createdAt: string;
}

export interface GetTeamMembersResponse {
  success: boolean;
  data: {
    members: TeamMember[];
    pendingInvitations: PendingInvitation[];
  };
}

// ==================== API Functions ====================

/**
 * Send team member invitation
 * @permission team:invite (Owner only)
 */
export async function inviteTeamMember(
  data: InviteTeamMemberRequest
): Promise<InviteResponse> {
  const response = await axiosInstance.post<InviteResponse>(
    '/api/team/invite',
    data
  );
  return response.data;
}

/**
 * Get all team members and pending invitations
 * @permission team:view (Owner/Admin)
 * @param branchId - Optional branch filter
 */
export async function getTeamMembers(branchId?: string): Promise<TeamMembersResponse> {
  const url = branchId 
    ? `/api/team/members?branch_id=${branchId}`
    : '/api/team/members';
  
  const response = await axiosInstance.get<TeamMembersResponse>(url);
  return response.data;
}

/**
 * Assign branches to team member
 * @permission team:manage (Owner only)
 */
export async function assignBranchesToMember(
  userId: string,
  data: AssignBranchesRequest
): Promise<AssignBranchesResponse> {
  const response = await axiosInstance.post<AssignBranchesResponse>(
    `/api/team/members/${userId}/branches`,
    data
  );
  return response.data;
}

/**
 * Remove branch assignment from team member
 * @permission team:manage (Owner only)
 */
export async function removeBranchFromMember(
  userId: string,
  branchId: string
): Promise<RemoveBranchResponse> {
  const response = await axiosInstance.delete<RemoveBranchResponse>(
    `/api/team/members/${userId}/branches/${branchId}`
  );
  return response.data;
}

/**
 * Get all branches assigned to a team member
 * @permission team:view
 */
export async function getMemberBranches(
  userId: string
): Promise<MemberBranchesResponse> {
  const response = await axiosInstance.get<MemberBranchesResponse>(
    `/api/team/members/${userId}/branches`
  );
  return response.data;
}

/**
 * Get all members assigned to a branch
 * @permission team:view
 */
export async function getBranchMembers(
  branchId: string
): Promise<BranchMembersResponse> {
  const response = await axiosInstance.get<BranchMembersResponse>(
    `/api/team/branches/${branchId}/members`
  );
  return response.data;
}

/**
 * Get all branches for current user's business
 * Used for branch selection in team management
 */
export async function getBranches(): Promise<{
  success: boolean;
  data: Array<{
    id: string;
    branch_code: string;
    branch_name: string;
    is_active: boolean;
  }>;
}> {
  const response = await axiosInstance.get('/api/v2/business/branches');
  return response.data;
}

/**
 * Accept team invitation and create account (Public endpoint)
 * @param token - Invitation token from email
 * @param data - User registration data
 */
export async function acceptInvitation(
  token: string,
  data: Omit<AcceptInvitationRequest, 'token'>
): Promise<AcceptInvitationResponse> {
  const response = await axiosInstance.post<AcceptInvitationResponse>(
    `/api/team/accept/${token}`,
    data
  );
  return response.data;
}

/**
 * Update team member role
 * @permission team:manage (Owner only)
 */
export async function updateMemberRole(
  userId: string,
  data: UpdateMemberRoleRequest
): Promise<UpdateRoleResponse> {
  const response = await axiosInstance.put<UpdateRoleResponse>(
    `/api/team/members/${userId}/role`,
    data
  );
  return response.data;
}

/**
 * Remove team member
 * @permission team:manage (Owner only)
 */
export async function removeMember(
  userId: string
): Promise<RemoveMemberResponse> {
  const response = await axiosInstance.delete<RemoveMemberResponse>(
    `/api/team/members/${userId}`
  );
  return response.data;
}

/**
 * Cancel pending invitation
 * @permission team:invite (Owner only)
 */
export async function cancelInvitation(
  invitationId: string
): Promise<CancelInvitationResponse> {
  const response = await axiosInstance.delete<CancelInvitationResponse>(
    `/api/team/invitations/${invitationId}`
  );
  return response.data;
}

// ==================== Helper Functions ====================

/**
 * Get role display name in Thai
 */
export function getRoleDisplayName(roleCode: 'owner' | 'admin' | 'staff' | 'customer_admin' | 'customer_manager' | 'customer_user'): string {
  const roleNames = {
    owner: 'เจ้าของ',
    admin: 'ผู้ดูแลระบบ',
    staff: 'พนักงาน',
    customer_admin: 'ผู้ดูแลระบบ',
    customer_manager: 'ผู้จัดการ',
    customer_user: 'พนักงาน',
  };
  return roleNames[roleCode];
}

/**
 * Get role badge color
 */
export function getRoleBadgeColor(roleCode: 'owner' | 'admin' | 'staff' | 'customer_admin' | 'customer_manager' | 'customer_user'): string {
  const colors = {
    owner: 'bg-purple-100 text-purple-800 border-purple-200',
    admin: 'bg-blue-100 text-blue-800 border-blue-200',
    staff: 'bg-green-100 text-green-800 border-green-200',
    customer_admin: 'bg-blue-100 text-blue-800 border-blue-200',
    customer_manager: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    customer_user: 'bg-green-100 text-green-800 border-green-200',
  };
  return colors[roleCode];
}

/**
 * Format invitation expiration date
 */
export function formatInvitationExpiry(expiresAt: string): string {
  const expiry = new Date(expiresAt);
  const now = new Date();
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'หมดอายุแล้ว';
  } else if (diffDays === 0) {
    return 'หมดอายุวันนี้';
  } else if (diffDays === 1) {
    return 'หมดอายุพรุ่งนี้';
  } else {
    return `เหลืออีก ${diffDays} วัน`;
  }
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Check if user has permission
 */
export function hasPermission(
  permissions: string[] | undefined,
  requiredPermission: string
): boolean {
  if (!permissions) return false;
  return permissions.includes(requiredPermission);
}
